import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, LogOut, Mail, Loader2, AlertTriangle } from 'lucide-react';
import { useTrialStatus } from '../../contexts/TrialStatusContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBilling } from '../../contexts/BillingContext';
import { APP_CONFIG } from '../../config/appConfig';
import { goToManagedPricing } from '../../utils/shopifyBilling';

// Plan-RemoveFreeTrial.md (Void Phase 1) — onboarding gate for a brand-new
// workspace created WITHOUT a trial. The trial grant was removed for new
// signups (`back/config/plans.py: TRIAL_ENABLED = False`); such workspaces
// start LOCKED at `subscription_status === "incomplete"` (plan=null,
// trial_ends_at=null) and are blocked by the backend's 402
// `reason:"subscription_required"` until the owner picks a plan + pays.
//
// This is the framing-only sibling of TrialExpiredBlocker.tsx — same
// full-screen z-40 placement, same Shopify/Stripe redirect logic, same
// owner / non-owner / sign-out structure — but the copy is ONBOARDING
// ("choose a plan to get started"), never "your trial ended". Existing
// trialing workspaces never reach this gate (their status is "trialing",
// not "incomplete"), so the unchanged TrialExpiredBlocker still serves them.
const IS_SHOPIFY_BUILD = APP_CONFIG.billingProvider === 'shopify';

const SubscriptionRequiredGate: React.FC = () => {
  const navigate = useNavigate();
  const { isOwner, ownerEmail, workspaceName } = useTrialStatus();
  const { logout } = useAuth();
  const { billing } = useBilling();

  const heading = 'Choose a plan to get started.';

  // Shopify build: the activate CTA resolves the hosted Managed Pricing
  // URL and redirects there. Mirrors TrialExpiredBlocker.handleUpgrade.
  const [redirecting, setRedirecting] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);

  // Plan-SwitchToBillingAPI.md — backend tells us which Shopify flow to use
  // at runtime via `billing.billing_mode`. Defaults to `managed` when absent.
  const billingMode: 'managed' | 'api' = billing?.billing_mode ?? 'managed';

  const handleChoosePlan = async () => {
    // Stripe build: unchanged — go to the in-app billing page where the
    // Stripe plan-change/checkout flow lives.
    if (!IS_SHOPIFY_BUILD) {
      navigate('/billing');
      return;
    }
    // Shopify build + Billing API mode: the picker is inline on /billing.
    if (billingMode === 'api') {
      navigate('/billing');
      return;
    }
    // Shopify build + Managed Pricing (or billing not loaded yet): redirect
    // to the hosted Managed Pricing page directly.
    setRedirecting(true);
    setNotConfigured(false);
    const result = await goToManagedPricing();
    if (result.status === 'not_configured') {
      setNotConfigured(true);
      setRedirecting(false);
    } else if (result.status === 'error') {
      // Fall back to the in-app billing page so the owner is never stuck.
      setRedirecting(false);
      navigate('/billing');
    }
    // `redirecting` → window is navigating away; leave the spinner up.
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose a plan to activate your workspace"
      className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
    >
      <div className="glass w-full max-w-md rounded-2xl border border-white/20 shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center shrink-0">
            <Rocket className="w-6 h-6 text-primary" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
              {heading}
            </h2>
            {workspaceName && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                Workspace: {workspaceName}
              </p>
            )}
          </div>
        </div>

        {isOwner ? (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
              Pick a plan to activate this workspace and bring your agents
              online. Our team provides fast support to get you up and running
              quickly.
            </p>
            {notConfigured && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 mb-4 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>
                  Billing isn&apos;t configured for this store yet. Please
                  check back shortly, or reach out to support.
                </span>
              </div>
            )}
            <button
              onClick={handleChoosePlan}
              disabled={redirecting}
              className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-purple-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {redirecting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              Choose a plan
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Your workspace owner needs to choose a plan to activate this
              workspace. Please reach out to them.
            </p>
            {ownerEmail ? (
              <a
                href={`mailto:${ownerEmail}`}
                className="inline-flex items-center gap-2 text-sm text-primary dark:text-primary hover:underline mb-6 break-all"
              >
                <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{ownerEmail}</span>
              </a>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-6">
                Contact an admin in your workspace to choose a plan.
              </p>
            )}
          </>
        )}

        {/*
          Escape hatch: a muted sign-out link so a user who landed on the
          wrong workspace (or simply wants to leave) is never trapped.
          Sidebar workspace switcher is the primary pivot path for
          multi-workspace users; this is the fallback.
        */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-center">
          <button
            onClick={logout}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionRequiredGate;
